import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@chakra-ui/react';
import { fetchApi } from '../../lib/apiClient';

// 邮件格式枚举
export enum EmailFormat {
  HTML = 'HTML',
  PLAIN = 'PLAIN',
  MARKDOWN = 'MARKDOWN'
}

// 测试结果接口
interface MailgunTestResult {
  success: boolean;
  message: string;
  details?: {
    messageId?: string;
    recipient?: string;
    error?: string;
  };
}

// 测试请求接口
interface MailgunTestRequest {
  title: string;
  body: string;
  format: EmailFormat;
}

/**
 * Mailgun 邮件测试面板组件
 */
export const MailgunTestPanel: React.FC = () => {
  const { t } = useTranslation(['v3/developerSettings', 'common']);
  const toast = useToast();

  // 表单状态
  const [title, setTitle] = useState(t('v3/developerSettings:mailgunTest.defaultTitle'));
  const [body, setBody] = useState(t('v3/developerSettings:mailgunTest.defaultContent') + new Date().toLocaleString());
  const [format, setFormat] = useState<EmailFormat>(EmailFormat.HTML);
  
  // 发送状态
  const [isSending, setIsSending] = useState(false);
  const [lastResult, setLastResult] = useState<MailgunTestResult | null>(null);

  // 格式选项
  const formatOptions = [
    { value: EmailFormat.HTML, label: t('v3/developerSettings:mailgunTest.formatOptions.html') },
    { value: EmailFormat.PLAIN, label: t('v3/developerSettings:mailgunTest.formatOptions.plain') },
    { value: EmailFormat.MARKDOWN, label: t('v3/developerSettings:mailgunTest.formatOptions.markdown') }
  ];

  // 发送测试邮件
  const handleSendTest = async () => {
    if (!title.trim() || !body.trim()) {
      toast({
        title: t('v3/developerSettings:mailgunTest.toast.fillRequired'),
        description: t('v3/developerSettings:mailgunTest.toast.fillRequiredDesc'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });
      return;
    }

    setIsSending(true);
    setLastResult(null);

    try {
      const requestData: MailgunTestRequest = {
        title: title.trim(),
        body: body.trim(),
        format
      };

      const response = await fetchApi<MailgunTestResult>('/mailgun/test', {
        method: 'POST',
        body: requestData
      });

      setLastResult(response);

      if (response.success) {
        toast({
          title: t('v3/developerSettings:mailgunTest.toast.sendSuccess'),
          description: t('v3/developerSettings:mailgunTest.toast.sendSuccessDesc'),
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top'
        });
      } else {
        toast({
          title: t('v3/developerSettings:mailgunTest.toast.sendFailed'),
          description: response.message || t('v3/developerSettings:mailgunTest.toast.unknownError'),
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top'
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('v3/developerSettings:mailgunTest.toast.networkError');
      const failureResult: MailgunTestResult = {
        success: false,
        message: errorMessage,
        details: { error: errorMessage }
      };
      
      setLastResult(failureResult);

      toast({
        title: '❌ 发送请求失败',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top'
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="mb-6">
      <div className="bg-white dark:bg-ios-dark-gray6 rounded-ios-lg overflow-hidden border border-ios-gray5 dark:border-ios-dark-gray5 shadow-ios dark:shadow-ios-dark p-4">
        <h2 className="text-ios-title3 font-semibold mb-3">{t('v3/developerSettings:mailgunTest.title')}</h2>
        <p className="text-ios-subhead text-ios-gray dark:text-ios-dark-gray mb-4">
          {t('v3/developerSettings:mailgunTest.description')}
        </p>

        {/* 邮件标题 */}
        <div className="mb-4">
          <label className="block text-ios-subhead font-medium mb-2">{t('v3/developerSettings:mailgunTest.emailTitle')}</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('v3/developerSettings:mailgunTest.emailTitlePlaceholder')}
            className="w-full px-3 py-2 border border-ios-gray5 dark:border-ios-dark-gray5 rounded-ios-md bg-white dark:bg-ios-dark-gray5 text-ios-body focus:outline-none focus:ring-2 focus:ring-ios-blue dark:focus:ring-ios-dark-blue"
          />
        </div>

        {/* 邮件内容 */}
        <div className="mb-4">
          <label className="block text-ios-subhead font-medium mb-2">{t('v3/developerSettings:mailgunTest.emailContent')}</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={t('v3/developerSettings:mailgunTest.emailContentPlaceholder')}
            rows={4}
            className="w-full px-3 py-2 border border-ios-gray5 dark:border-ios-dark-gray5 rounded-ios-md bg-white dark:bg-ios-dark-gray5 text-ios-body focus:outline-none focus:ring-2 focus:ring-ios-blue dark:focus:ring-ios-dark-blue resize-vertical"
          />
        </div>

        {/* 邮件格式 */}
        <div className="mb-4">
          <label className="block text-ios-subhead font-medium mb-2">{t('v3/developerSettings:mailgunTest.emailFormat')}</label>
          <div className="flex gap-2">
            {formatOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setFormat(option.value)}
                className={`px-3 py-2 rounded-ios-md text-sm font-medium transition-colors ${
                  format === option.value
                    ? 'bg-ios-blue dark:bg-ios-dark-blue text-white'
                    : 'bg-ios-gray6 dark:bg-ios-dark-gray5 text-ios-body dark:text-ios-dark-body hover:bg-ios-gray5 dark:hover:bg-ios-dark-gray4'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* 发送按钮 */}
        <div className="mb-4">
          <button
            onClick={handleSendTest}
            disabled={isSending || !title.trim() || !body.trim()}
            className="w-full px-4 py-3 bg-ios-blue dark:bg-ios-dark-blue text-white rounded-ios-md text-ios-body font-medium hover:bg-ios-blue-dark dark:hover:bg-ios-dark-blue-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSending ? (
              <div className="flex items-center justify-center">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                {t('v3/developerSettings:mailgunTest.sending')}
              </div>
            ) : (
              t('v3/developerSettings:mailgunTest.sendButton')
            )}
          </button>
        </div>

        {/* 测试结果 */}
        {lastResult && (
          <div className="bg-ios-gray6 dark:bg-ios-dark-gray5 rounded-ios-md p-3">
            <h4 className="text-ios-subheadline font-medium mb-2">{t('v3/developerSettings:mailgunTest.testResult')}</h4>
            <div className="space-y-2 text-ios-subhead">
              <div>
                <span className="text-ios-gray dark:text-ios-dark-gray">{t('v3/developerSettings:mailgunTest.status')}{t('v3/developerSettings:common.units.colon')}</span>
                <span className={`font-medium ml-1 ${
                  lastResult.success
                    ? 'text-ios-green dark:text-ios-dark-green'
                    : 'text-ios-red dark:text-ios-dark-red'
                }`}>
                  {lastResult.success ? t('v3/developerSettings:mailgunTest.sendSuccess') : t('v3/developerSettings:mailgunTest.sendFailed')}
                </span>
              </div>
              <div>
                <span className="text-ios-gray dark:text-ios-dark-gray">{t('v3/developerSettings:mailgunTest.message')}{t('v3/developerSettings:common.units.colon')}</span>
                <span className="font-medium ml-1">{lastResult.message}</span>
              </div>
              {lastResult.details?.messageId && (
                <div>
                  <span className="text-ios-gray dark:text-ios-dark-gray">{t('v3/developerSettings:mailgunTest.messageId')}{t('v3/developerSettings:common.units.colon')}</span>
                  <span className="font-medium ml-1 text-ios-footnote font-mono">{lastResult.details.messageId}</span>
                </div>
              )}
              {lastResult.details?.recipient && (
                <div>
                  <span className="text-ios-gray dark:text-ios-dark-gray">{t('v3/developerSettings:mailgunTest.recipient')}{t('v3/developerSettings:common.units.colon')}</span>
                  <span className="font-medium ml-1">{lastResult.details.recipient}</span>
                </div>
              )}
              {lastResult.details?.error && (
                <div>
                  <span className="text-ios-gray dark:text-ios-dark-gray">{t('v3/developerSettings:mailgunTest.errorDetails')}{t('v3/developerSettings:common.units.colon')}</span>
                  <span className="font-medium ml-1 text-ios-red dark:text-ios-dark-red">{lastResult.details.error}</span>
                </div>
              )}
              <div>
                <span className="text-ios-gray dark:text-ios-dark-gray">{t('v3/developerSettings:mailgunTest.testTime')}{t('v3/developerSettings:common.units.colon')}</span>
                <span className="font-medium ml-1 text-ios-footnote">{new Date().toLocaleString()}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MailgunTestPanel;
